import type { VariantAtom } from '@core/Entities/Search/Atoms/AvailableVariantsAtom.entity';
import type { FilterParams } from '@core/Entities/Search/QueryParams.entity';
import type { Filter } from '@core/Entities/Search/SearchFilters/Filter.entity';
import { RANGE_FILTERS } from '@core/Services/Search/Filters.constants';
import { BODY_TYPES } from '@core/Utils/VehicleMappedMessages/BodyTypeMappings.constants';
import { COLORS } from '@core/Utils/VehicleMappedMessages/ColorMappings.constants';
import { FUEL_TYPES } from '@core/Utils/VehicleMappedMessages/FuelTypeMappings.constants';
import { getOptionsWithAggs } from '@plp/Utils/Filters/GetOptionsWithAggs';

import type { SectionConfig, SectionOption } from './VehicleInterlinking.entities';

export const CARRY_OVER_FILTERS = [
  'fuel-type',
  'gear-box',
  'body-type',
  'color',
  'engine-size__gte',
  'engine-size__lte',
] as const;

const getActiveFilterParams = (combinedParams: FilterParams | undefined): Filter[] =>
  CARRY_OVER_FILTERS.flatMap(id =>
    combinedParams?.[id] ? [{ id, value: combinedParams[id] }] : [],
  );

const getMakeModelFilters = (make: string | undefined, modelWithMake: string | undefined) => [
  ...(make ? [{ id: 'make' as const, value: make }] : []),
  ...(modelWithMake ? [{ id: 'model' as const, value: modelWithMake }] : []),
];

const joinCtaText = (...parts: (string | undefined)[]) => parts.filter(Boolean).join(' ');

const excludeActiveOption = <T extends { id: string; labelKey?: string }>(
  options: T[],
  activeValue: string | undefined,
) => options.filter(option => option.id !== activeValue && option.labelKey !== activeValue);

const modelsConfig: SectionConfig = {
  key: 'models',
  headingKey: 'modelsForSale',
  messageKey: null,
  getOptions: (aggs, make) => {
    if (!make || !aggs.model?.[make]?.models) return [];
    return Object.entries(aggs.model[make].models)
      .map(([id, model]) => ({ id, count: model.count, label: model.label }))
      .filter(({ count }) => count > 0);
  },
  createFilters: (makeName, _, modelId, combinedParams) => [
    ...getMakeModelFilters(makeName, modelId),
    ...getActiveFilterParams(combinedParams),
  ],
  formatCtaText: (makeDisplay, _, id, activeFilterDisplayName) =>
    joinCtaText(makeDisplay, id, activeFilterDisplayName),
  getDisplayName: option => option.label || option.id,
};

const pricesConfig: SectionConfig = {
  key: 'prices',
  headingKey: 'prices',
  messageKey: null,
  getOptions: aggs => {
    const priceBuckets = aggs.price ?? {};
    let eligibleVehicleCount = 0;
    const highestPrice = Object.keys(priceBuckets).sort((a, b) => Number(b) - Number(a))[0];
    const ceilingPrice =
      RANGE_FILTERS.price.find(price => Number(price) > Number(highestPrice)) ??
      RANGE_FILTERS.price.at(-1);
    return RANGE_FILTERS.price.flatMap(price => {
      eligibleVehicleCount += priceBuckets[price] ?? 0;

      return Number(price) <= Number(ceilingPrice) && eligibleVehicleCount > 0
        ? [
            {
              id: price,
              count: eligibleVehicleCount,
              label: `Under £${Number(price).toLocaleString('en-GB')}`,
            },
          ]
        : [];
    });
  },
  createFilters: (makeName, modelWithMakeName, priceLte) => [
    ...getMakeModelFilters(makeName, modelWithMakeName),
    { id: 'price__lte', value: priceLte },
  ],
  formatCtaText: (makeDisplay, modelDisplay, priceDisplay) =>
    joinCtaText(makeDisplay, modelDisplay, priceDisplay),
  getDisplayName: option => option.label || option.id,
};

const makesConfig: SectionConfig = {
  key: 'makes',
  headingKey: 'makesForSale',
  messageKey: null,
  getOptions: aggs => {
    return Object.entries(aggs.make ?? {})
      .map(([id, make]) => ({
        id,
        count: make.count,
        label: make.label,
      }))
      .filter(({ count }) => count > 0);
  },
  createFilters: (_, __, makeId, combinedParams) => [
    { id: 'make', value: makeId },
    ...getActiveFilterParams(combinedParams),
  ],
  formatCtaText: (_, __, makeDisplayName, activeFilterDisplayName) =>
    joinCtaText(makeDisplayName, activeFilterDisplayName),
  getDisplayName: option => option.label || option.id,
};

const trimsConfig: SectionConfig = {
  key: 'trims',
  headingKey: 'modelVariants',
  messageKey: null,
  getOptions: (aggs, makeName, modelWithMakeName) => {
    if (!makeName || !modelWithMakeName) return [];
    // First try to use the flattened variants array
    if (aggs.variants && aggs.variants.length > 0) {
      const filteredVariants = aggs.variants.filter(
        (v: VariantAtom) => v.make === makeName && v.model === modelWithMakeName,
      );

      if (filteredVariants.length > 0) {
        return filteredVariants.map((variant: VariantAtom) => ({
          id: variant.key,
          count: variant.count,
          label: variant.displayName,
        }));
      }
    }

    // Fall back to using the nested variant structure if available
    if (modelWithMakeName && aggs.variant?.[makeName]?.models[modelWithMakeName]?.variants) {
      const variants = aggs.variant[makeName].models[modelWithMakeName].variants;
      return Object.entries(variants).reduce((acc, [variantId, variant]) => {
        if (variant.count > 0) {
          acc.push({ id: variantId, count: variant.count, label: variant.label });
        }
        return acc;
      }, [] as SectionOption[]);
    }

    return [];
  },
  createFilters: (makeName, modelWithMakeName, variantId, combinedParams) => [
    ...getMakeModelFilters(makeName, modelWithMakeName),
    { id: 'variant-type', value: variantId },
    ...getActiveFilterParams(combinedParams),
  ],
  formatCtaText: joinCtaText,
  getDisplayName: option => option.label || option.id,
};

export const bodyTypesConfig: SectionConfig = {
  key: 'body-types',
  headingKey: 'bodyTypes',
  messageKey: 'body-types',
  getOptions: (aggs, _make, _model, combinedParams) => {
    if (!aggs['body-type']) return [];
    return excludeActiveOption(
      getOptionsWithAggs(BODY_TYPES, aggs['body-type']),
      combinedParams?.['body-type']?.[0],
    );
  },
  createFilters: (makeName, modelWithMakeName, bodyTypeId) => [
    ...getMakeModelFilters(makeName, modelWithMakeName),
    { id: 'body-type', value: bodyTypeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, bodyTypeDisplay) =>
    joinCtaText(makeDisplay, modelDisplay, bodyTypeDisplay),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.labelKey as keyof typeof messagesObj] || option.id,
};

const fuelTypesConfig: SectionConfig = {
  key: 'fuel-types',
  headingKey: 'fuelType',
  messageKey: 'fuel-types',
  getOptions: (aggs, _make, _model, combinedParams) => {
    if (!aggs['fuel-type']) return [];
    return excludeActiveOption(
      getOptionsWithAggs(FUEL_TYPES, aggs['fuel-type']),
      combinedParams?.['fuel-type']?.[0],
    );
  },
  createFilters: (makeName, modelWithMakeName, fuelTypeId) => [
    ...getMakeModelFilters(makeName, modelWithMakeName),
    { id: 'fuel-type', value: fuelTypeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, fuelTypeDisplay) =>
    joinCtaText(makeDisplay, modelDisplay, fuelTypeDisplay),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.labelKey as keyof typeof messagesObj] || option.id,
};

const filterOutTransmissions = new Set<string>(['unknown', 'none']);

const transmissionsConfig: SectionConfig = {
  key: 'transmissions',
  headingKey: 'transmissions',
  messageKey: 'transmissions',
  getOptions: (aggs, _make, _model, combinedParams) => {
    if (!aggs['gear-box']) return [];

    return Object.entries(aggs['gear-box'])
      .map(([id, count]) => ({ id, count: count as number, label: id }))
      .filter(
        ({ count, id }) =>
          count > 0 && id !== combinedParams?.['gear-box']?.[0] && !filterOutTransmissions.has(id),
      );
  },
  createFilters: (makeName, modelWithMakeName, gearboxId) => [
    ...getMakeModelFilters(makeName, modelWithMakeName),
    { id: 'gear-box', value: gearboxId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, gearboxDisplay) =>
    joinCtaText(makeDisplay, modelDisplay, gearboxDisplay),
  getDisplayName: (option, messagesObj) =>
    messagesObj[option.id as keyof typeof messagesObj] || option.id,
};

const engineSizesConfig: SectionConfig = {
  key: 'engine-sizes',
  headingKey: 'engineSizes',
  messageKey: null,
  getOptions: aggs => {
    if (!aggs['engine-size']) return [];
    return Object.entries(aggs['engine-size'])
      .map(([id, count]) => ({ id, count: count as number, label: id }))
      .filter(({ count }) => count > 0);
  },
  createFilters: (makeName, modelWithMakeName, engineSizeId) => [
    ...getMakeModelFilters(makeName, modelWithMakeName),
    { id: 'engine-size__gte', value: engineSizeId },
    { id: 'engine-size__lte', value: engineSizeId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, engineSizeDisplay) =>
    joinCtaText(makeDisplay, modelDisplay, engineSizeDisplay),
  getDisplayName: option => option.id,
};

// "other" color has different ids in different locales
const otherColorIds = new Set(['othercolor', 'other']);

const colorsConfig: SectionConfig = {
  key: 'colors',
  headingKey: 'colors',
  messageKey: 'colors',
  getOptions: (aggs, _make, _model, combinedParams) => {
    if (!aggs['color']) return [];
    return excludeActiveOption(
      getOptionsWithAggs(COLORS, aggs.color),
      combinedParams?.['color']?.[0],
    ).filter(option => !otherColorIds.has(option.id) && !otherColorIds.has(option.labelKey));
  },
  createFilters: (makeName, modelWithMakeName, colorId) => [
    ...getMakeModelFilters(makeName, modelWithMakeName),
    { id: 'color', value: colorId },
  ],
  formatCtaText: (makeDisplay, modelDisplay, colorDisplay) =>
    joinCtaText(makeDisplay, modelDisplay, colorDisplay),
  getDisplayName: (option, messagesObj) => messagesObj[`${option.labelKey}.one`] || option.id,
};

export const MAKE_MODEL_INTERLINKING_SECTIONS: SectionConfig[] = [
  trimsConfig,
  pricesConfig,
  bodyTypesConfig,
  fuelTypesConfig,
  transmissionsConfig,
  engineSizesConfig,
  colorsConfig,
];

export const MAKE_INTERLINKING_SECTIONS: SectionConfig[] = [
  modelsConfig,
  pricesConfig,
  bodyTypesConfig,
  transmissionsConfig,
  fuelTypesConfig,
];

export const PLP_VEHICLE_INTERLINKING_SECTIONS: SectionConfig[] = [
  makesConfig,
  pricesConfig,
  bodyTypesConfig,
  transmissionsConfig,
  fuelTypesConfig,
];
